import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const QuickAnswersFAQ = () => {
  const faqItems = [
    {
      id: 'synthetic-contribution',
      question: "Paying conversion tax from my checking account is actually a \"contribution in disguise\"!",
      answer: (
        <>
          <strong>Yes!</strong> While labeled a "tax payment," using non-retirement funds to pay for a Roth conversion is economically identical to making a Roth contribution 
          (<strong>Synthetic Roth Contribution</strong>). When you pay the IRS $25,000 from your savings to convert an IRA/401k, you are effectively "moving" $25,000 of your taxable wealth 
          into a tax-sheltered vehicle. The question for the investor isn't "Can I afford the tax?" but rather "Do I want to make 
          an optional contribution to my Roth account today at the expense of my current liquidity?" If you wouldn't make a standard Roth contribution this year because you 
          need the cash, you shouldn't pay for a conversion from the outside either.  Caveat: If you're younger than 59 1/2, you should pay with outside funds and avoid the 10% early withdrawal penalty
          you would incur if paying the tax from inside the Traditional account.
        </>
      )
    },
    {
      id: 'inside-outside',
      question: "What's better for reducing RMDs and profiting from my low tax rate today, outside- or inside-funded conversions?",
      answer: (
        <>
          <strong>Both!</strong> Inside- and Outside-funded Roth conversions reduce RMDs equally and both capture equal spread profits by converting at a lower rate today than distributing 
          at tomorrow's higher tax rate. CAVEAT: Inside conversions made before age 59 1/2 incur a 10% early withdrawal penalty. If you're 58 or younger and contemplating a Roth conversion, 
          then paying the tax with outside funds is the better choice. If you're 59 1/2, the ONLY DIFFERENCE between inside- and outside-funded conversions is that your outside-funded tax 
          payment is effectively a Synthetic Roth contribution.
        </>
      )
    },
    {
      id: 'profitability',
      question: "How do I know if my conversion will be profitable?",
      answer: (
        <>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Enter your data and assumptions in the <strong>CONVERSIONS</strong> tab, then look at the Roth conversion tables.  Conversions that are profitable will have a positive amount in "<strong>Net PV</strong>" column, and
            the entire row should be green to indicate a profitable conversion. If the row is red, the conversion is unprofitable.
          </Typography>
        </>
      )
    },
    {
      id: 'distribution-rate',
      question: "What tax rate should I use for distribution planning?",
      answer: (
        <>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We should use the weighted future tax rate that results from distributing the entirety of our taxable Traditional savings accounts.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Thankfully, <strong>RothGPT</strong> does this for you based upon your inputs. No guessing.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Fidelity, Schwab and Vanguard calculators do not calculate your future tax rate but ask the user to input their 'guess' for this input. This is a major problem in the financial industry since most investors do not have the tools to accurately estimate their future rate.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>RothGPT</strong> uses your Traditional balance and your Social Security benefit to project your future income for every year until the Traditional account has been fully depleted. Tax brackets, deductions, social security, etc are inflation adjusted through time to give best estimates of the user's future tax rate. Return rate and investor lifetime estimates also factor into the calculations.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Selecting the '<strong>Fixed Annuity</strong>' Distribution Method produces equal annual distributions during the investors selected life period that fully depletes the account by the end of that period. Selecting the '<strong>RMD Only</strong>' Distribution Method is for those who do not need the yearly income and wish to maximize account value left to heirs. This method includes a 10 year heir distribution period before the account is fully depleted.
          </Typography>
        </>
      )
    },
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 0, fontWeight: 'bold', color: 'primary.main' }}>
        Facts & FAQs
      </Typography>
      {faqItems.map((faq) => (
        <Accordion key={faq.id} sx={{ mb: 1, '&.Mui-expanded': { mb: 1, mt: 0 } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#e3f2fd',
              minHeight: 'unset',
              '&.Mui-expanded': { minHeight: 'unset' },
              '& .MuiAccordionSummary-content': { my: 1 },
              '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 },
              '&:hover': {
                backgroundColor: '#bbdefb'
              }
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: '#f5f5f5' }}>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default QuickAnswersFAQ;
